import { useQuery } from '@tanstack/react-query';
import { api, type DashboardAlertSummary, type WorkOrder, type Client, type ServiceLog, type RouteAlert, type AgingClient, type MissingDataClient, type SuspiciousDataClient, type SuspiciousLocationAlert, type PendingInventoryReview } from '../lib/api';
import { useBranchStore } from '../stores/branchStore';

// Alert type identifiers
export type AlertType =
  // Admin alerts
  | 'admin-missing-data'
  | 'admin-no-route'
  | 'admin-route-not-scheduled'
  | 'admin-suspicious-data'
  | 'admin-suspicious-locations'
  // Client alerts
  | 'client-aging'
  | 'client-missed-service'
  | 'client-partial-missed'
  | 'client-no-next-visit'
  // Workorder alerts
  | 'wo-urgent'
  | 'wo-stalled'
  | 'wo-ready-pickup'
  // Admin — inventory
  | 'admin-inventory-review';

export type AlertCategory = 'admin' | 'clients' | 'workorders';

// Severity levels for visual indicators
export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

// Alert metadata for display
export interface AlertTypeInfo {
  id: AlertType;
  label: string;
  category: AlertCategory;
  icon: string;
  description: string;
  severity: AlertSeverity;
}

// All available alert types with metadata
// Ordered by display priority within each category (per Item 5):
// - CLIENTS: Aging, No Next Visit, Missed Service, Partial Missed
// - WORKORDERS: Urgent, Ready for Action, Stalled
// - ADMIN: No Route, Route Not Scheduled, Missing Data, Suspicious Data, Suspicious Locations
export const ALERT_TYPES: Record<AlertType, AlertTypeInfo> = {
  // === CLIENTS (displayed first) ===
  'client-aging': {
    id: 'client-aging',
    label: 'Aging',
    category: 'clients',
    icon: '⏰',
    description: 'Clients whose last service exceeds their expected interval',
    severity: 'high',
  },
  'client-no-next-visit': {
    id: 'client-no-next-visit',
    label: 'No Next Visit',
    category: 'clients',
    icon: '📆',
    description: 'Active clients with no scheduled service in horizon',
    severity: 'high',
  },
  'client-missed-service': {
    id: 'client-missed-service',
    label: 'Missed Service',
    category: 'clients',
    icon: '❌',
    description: 'Service stops flagged as fully missed',
    severity: 'critical',
  },
  'client-partial-missed': {
    id: 'client-partial-missed',
    label: 'Partial Missed',
    category: 'clients',
    icon: '⚠️',
    description: 'Service stops flagged as partial missed - requires follow-up',
    severity: 'high',
  },
  // === WORKORDERS (displayed second) ===
  'wo-urgent': {
    id: 'wo-urgent',
    label: 'Urgent',
    category: 'workorders',
    icon: '🚨',
    description: 'Open workorders marked as urgent priority',
    severity: 'critical',
  },
  'wo-ready-pickup': {
    id: 'wo-ready-pickup',
    label: 'Ready for Action',
    category: 'workorders',
    icon: '🚚',
    description: 'Workorders in Ready for Pickup or Ready for Run status awaiting delivery',
    severity: 'low',
  },
  'wo-stalled': {
    id: 'wo-stalled',
    label: 'Stalled',
    category: 'workorders',
    icon: '⏳',
    description: 'Open workorders that have not progressed in threshold period',
    severity: 'critical',
  },
  // === ADMIN (displayed third) ===
  'admin-no-route': {
    id: 'admin-no-route',
    label: 'No Route',
    category: 'admin',
    icon: '🗺️',
    description: 'Active clients without route assignment',
    severity: 'medium',
  },
  'admin-route-not-scheduled': {
    id: 'admin-route-not-scheduled',
    label: 'Route Not Scheduled',
    category: 'admin',
    icon: '📅',
    description: 'Routes that have no upcoming scheduled execution',
    severity: 'high',
  },
  'admin-missing-data': {
    id: 'admin-missing-data',
    label: 'Missing Data',
    category: 'admin',
    icon: '📋',
    description: 'Active clients with incomplete required data fields',
    severity: 'medium',
  },
  'admin-suspicious-data': {
    id: 'admin-suspicious-data',
    label: 'Suspicious Data',
    category: 'admin',
    icon: '🔍',
    description: 'Clients with data quality issues (bad geocodes, invalid intervals, duplicates, tech mismatches)',
    severity: 'medium',
  },
  'admin-suspicious-locations': {
    id: 'admin-suspicious-locations',
    label: 'Suspicious Locations',
    category: 'admin',
    icon: '📍',
    description: 'Service logs created far from client location (>0.5 miles)',
    severity: 'high',
  },
  'admin-inventory-review': {
    id: 'admin-inventory-review',
    label: 'Inventory Review',
    category: 'admin',
    icon: '📦',
    description: 'Inventory edits pending billing review',
    severity: 'medium',
  },
};

// Get badge variant based on severity
export function getSeverityVariant(severity: AlertSeverity): 'danger' | 'earth' | 'warning' | 'info' {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'earth';
    case 'medium':
      return 'warning';
    case 'low':
      return 'info';
  }
}

// Get alerts for a category
export function getAlertsByCategory(category: AlertCategory): AlertTypeInfo[] {
  return Object.values(ALERT_TYPES).filter((alert) => alert.category === category);
}

// Hook to fetch dashboard alert summary
export function useDashboardSummary() {
  // Include branch in query key so data refetches when branch changes
  const branchId = useBranchStore((state) => state.getCurrentBranchForQuery());

  return useQuery({
    queryKey: ['dashboard', 'alerts', 'summary', { branchId }],
    queryFn: async () => {
      const response = await api.dashboard.getAlertsSummary();
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch dashboard summary');
      }
      return response.data as DashboardAlertSummary;
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

// Hook to fetch alert items for a specific alert type
export function useAlertItems(alertType: AlertType | null) {
  // Include branch in query key so data refetches when branch changes
  const branchId = useBranchStore((state) => state.getCurrentBranchForQuery());

  return useQuery({
    queryKey: ['dashboard', 'alerts', alertType, { branchId }],
    queryFn: async () => {
      if (!alertType) return null;

      switch (alertType) {
        case 'wo-urgent': {
          const response = await api.dashboard.getUrgentWorkorders();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'workorders' as const, items: response.data as WorkOrder[] };
        }
        case 'wo-stalled': {
          const response = await api.dashboard.getStalledWorkorders();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'workorders' as const, items: response.data as WorkOrder[] };
        }
        case 'wo-ready-pickup': {
          const response = await api.dashboard.getReadyForPickup();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'workorders' as const, items: response.data as WorkOrder[] };
        }
        case 'admin-no-route': {
          const response = await api.dashboard.getClientsNoRoute();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'clients' as const, items: response.data as Client[] };
        }
        case 'client-missed-service': {
          const response = await api.dashboard.getMissedServices();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'service-logs' as const, items: response.data as ServiceLog[] };
        }
        case 'client-partial-missed': {
          const response = await api.dashboard.getPartialMissedServices();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'service-logs' as const, items: response.data as ServiceLog[] };
        }
        case 'admin-route-not-scheduled': {
          const response = await api.dashboard.getRoutesNotScheduled();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'routes' as const, items: response.data as RouteAlert[] };
        }
        case 'client-no-next-visit': {
          const response = await api.dashboard.getClientsNoNextVisit();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'clients' as const, items: response.data as Client[] };
        }
        case 'client-aging': {
          const response = await api.dashboard.getAgingClients();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'aging-clients' as const, items: response.data as AgingClient[] };
        }
        case 'admin-missing-data': {
          const response = await api.dashboard.getClientsWithMissingData();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'missing-data-clients' as const, items: response.data as MissingDataClient[] };
        }
        case 'admin-suspicious-data': {
          const response = await api.dashboard.getSuspiciousData();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'suspicious-data-clients' as const, items: response.data as SuspiciousDataClient[] };
        }
        case 'admin-suspicious-locations': {
          const response = await api.dashboard.getSuspiciousLocations();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'suspicious-locations' as const, items: response.data as SuspiciousLocationAlert[] };
        }
        case 'admin-inventory-review': {
          const response = await api.inventory.getPendingReviews();
          if (!response.success) throw new Error(response.error?.message);
          return { type: 'inventory-review' as const, items: response.data as PendingInventoryReview[] };
        }
        default:
          return { type: 'empty' as const, items: [] };
      }
    },
    enabled: !!alertType,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Get count for a specific alert type from the summary
export function getAlertCount(
  summary: DashboardAlertSummary | undefined,
  alertType: AlertType
): number {
  if (!summary) return 0;

  switch (alertType) {
    case 'admin-missing-data':
      return summary.admin.missingData;
    case 'admin-no-route':
      return summary.admin.noRoute;
    case 'admin-route-not-scheduled':
      return summary.admin.routeNotScheduled;
    case 'admin-suspicious-data':
      return summary.admin.suspiciousData;
    case 'admin-suspicious-locations':
      return summary.admin.suspiciousLocations;
    case 'client-aging':
      return summary.clients.aging;
    case 'client-missed-service':
      return summary.clients.missedService;
    case 'client-partial-missed':
      return summary.clients.partialMissed;
    case 'client-no-next-visit':
      return summary.clients.noNextVisit;
    case 'wo-urgent':
      return summary.workorders.urgent;
    case 'wo-stalled':
      return summary.workorders.stalled;
    case 'wo-ready-pickup':
      return summary.workorders.readyForPickup;
    case 'admin-inventory-review':
      // Count comes from the dedicated pending-reviews endpoint, not the summary
      return 0;
    default:
      return 0;
  }
}

// Get category total count
export function getCategoryTotal(
  summary: DashboardAlertSummary | undefined,
  category: AlertCategory
): number {
  if (!summary) return 0;

  switch (category) {
    case 'admin':
      return summary.admin.total;
    case 'clients':
      return summary.clients.total;
    case 'workorders':
      return summary.workorders.total;
    default:
      return 0;
  }
}
